import { useNavigate, useSearch } from "@tanstack/react-router"
import { useQueryState, parseAsStringLiteral } from "nuqs"

import ExtractedDataTab from "./ExtractedDataTab"
import ExtractionJobsTab from "./ExtractionJobsTab"
import ExtractionStatsTab from "./ExtractionStatsTab"
import JobsTab from "./JobsTab"
import MonitorsTab from "./MonitorsTab"
import JobResultsTab from "./ResultsTab"
import WebsiteChangesTab from "./WebsiteChangesTab"

type ScrapingTab =
  | "monitors"
  | "jobs"
  | "results"
  | "extraction-jobs"
  | "extracted-data"
  | "extraction-stats"
  | "website-changes"

export default function ScrapingScreen() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { monitorSlug?: string; jobId?: string }

  const [tab, setTab] = useQueryState(
    "tab",
    parseAsStringLiteral([
      "monitors",
      "jobs",
      "results",
      "extraction-jobs",
      "extracted-data",
      "extraction-stats",
      "website-changes",
    ]).withDefault("monitors"),
  )

  const handleTabChange = (newTab: ScrapingTab) => {
    void setTab(newTab, { history: "push" })
    // Clear subsidiary params when switching away from relevant tabs
    if (newTab !== "jobs" && search.monitorSlug) {
      navigate({
        to: "/scraping",
        // @ts-ignore
        search: (prev: any) => ({ ...prev, monitorSlug: undefined }),
      })
    }
    if (newTab !== "results" && newTab !== "extracted-data" && search.jobId) {
      navigate({
        to: "/scraping",
        // @ts-ignore
        search: (prev: any) => ({ ...prev, jobId: undefined }),
      })
    }
  }

  return (
    <div className="min-h-screen bg-foregroundx">
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-background rounded-lg shadow-sm">
          <div className="border-b border-border px-6 py-4">
            <h1 className="text-2xl font-bold ">Scraping Dashboard</h1>
            <p className="text-sm text-primary mt-1">Monitor and manage your scraping operations</p>
          </div>

          <div className="border-b border-border">
            <nav className="flex space-x-8 px-6">
              <button
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  tab === "monitors"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
                onClick={() => handleTabChange("monitors")}
              >
                Monitors
              </button>
              <button
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  tab === "jobs"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
                onClick={() => handleTabChange("jobs")}
              >
                Jobs
              </button>
              <button
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  tab === "results"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
                onClick={() => handleTabChange("results")}
              >
                Job Results
              </button>
              <button
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  tab === "extraction-jobs"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
                onClick={() => handleTabChange("extraction-jobs")}
              >
                Extraction Jobs
              </button>
              <button
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  tab === "extracted-data"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
                onClick={() => handleTabChange("extracted-data")}
              >
                Extracted Data
              </button>
              <button
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  tab === "extraction-stats"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
                onClick={() => handleTabChange("extraction-stats")}
              >
                Extraction Stats
              </button>
              <button
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  tab === "website-changes"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
                onClick={() => handleTabChange("website-changes")}
              >
                Website Changes
              </button>
            </nav>
          </div>

          <div className="p-6">
            {tab === "monitors" && <MonitorsTab />}
            {tab === "jobs" && <JobsTab />}
            {tab === "results" && <JobResultsTab />}
            {tab === "extraction-jobs" && <ExtractionJobsTab />}
            {tab === "extracted-data" && <ExtractedDataTab />}
            {tab === "extraction-stats" && <ExtractionStatsTab />}
            {tab === "website-changes" && <WebsiteChangesTab />}
          </div>
        </div>
      </div>
    </div>
  )
}
